<template>
    <main class="blog">
        <page-hero title="Blog" subtitle="News, updates, and more!" />
        <div class="blog-post-wrapper">
            <article class="box">
                <section class="blog-post-items columns is-multiline">
                    <div class="blog-post" v-for="post in posts" :key="post.sys.id">
                        <div class="article-content">
                            <p class="has-text-gray blog-post-info">
                                <img loading="lazy" class="author-image" :src="setSize(post.fields.authorImage, 100)"/>
                                &nbsp;{{ post.fields.author }} on {{ post.fields.date.toLocaleDateString() }}<span v-if="post.fields.length"> | {{ post.fields.length }} min read</span>
                            </p>
                            <h2 class="title is-4 clickable" @click="() => route(post.sys.id)" aria-role="button">{{ post.fields.title }}</h2>
                            <p class="blog-preview clickable" @click="() => route(post.sys.id)" aria-role="button">{{ post.preview }}</p>
                            <router-link :to="`/post/${post.sys.id}`" class="link has-text-info">Read more</router-link>
                        </div>
                        <img loading="lazy" class="blog-image clickable" @click="() => route(post.sys.id)" aria-role="button" :src="setSize(post.fields.image, 600)"/>
                    </div>
                </section>
                <div class="end-of-blog">
                    <p class="has-text-centered has-text-gray">Sorry, you've reached the end of the blog. Thanks for reading!</p>
                </div>
            </article>
        </div>
    </main>
</template>

<style scoped lang="scss">

.clickable {
    cursor: pointer;
}

@media screen and (max-width: 768px) {
    .blog-post .blog-image {
        display: none;
    }
}

.blog {
    background-color: #eee;
    min-height: 700px;
    padding-bottom: 100px;

    .blog-post-wrapper {
        display: block;
        position: relative;
        max-width: $widescreen;
        margin: 0 auto;
        padding: 1.5rem;

        .blog-post-items {
            display: flex;
            background-color: white;

        
            .blog-post {
                display: flex;
                padding: 2rem;
                text-align: left;
                font-size: 1.25rem;

                align-items: center;

                .author-image {
                    width: 35px;
                    height: 35px;
                    object-fit: cover;
                    object-position: center;
                    border-radius: 50%;
                    transform: translateY(25%);
                    margin-right: 0.5rem;
                }

                .blog-post-info {
                    line-height: 50px;
                    vertical-align: middle;
                }
                
                .blog-image {
                    width: 300px;
                    height: auto;
                    object-fit: cover;
                    object-position: left;
                    border-radius: 0.5rem;
                }

                .title {
                    text-align: left;
                    margin-top: 1rem;
                    color: black;
                    letter-spacing: -0.05rem;

                }

                img {
                    object-fit: cover;
                    width: 300px;
                    border-radius: 0.5rem
                }

                .blog-preview {
                    margin-top: 1rem;
                    color: #666;
                    margin-bottom: 1rem;
                    padding-right: 1rem;
                }

                &:not(:last-child) {
                    border-bottom: 0.5px solid $gray-light;
                }
            }

            min-height: 450px;
        }
    }

}

</style>

<script>
import PageHero from '../components/Page/PageHero.vue'

const SPACE_ID = 'oe89s2s3rmwc'
const ENVIRONMENT_ID = 'master'
const FALLBACK_IMAGE = '/images/social-graphic.png'

export default {
    name: 'Blog',
    components: {
        PageHero
    },

    data() {
        return {
            allPosts: [],
            posts: [],
            includes: {},
        }
    },
    mounted() {
        this.getPosts()

        let observer = new IntersectionObserver(function(entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.updatePosts.bind(this)()
                    if(this.posts.length >= this.allPosts.length) {
                        observer.unobserve(entry.target)
                    }
                }
            })
        }.bind(this))

        let target = document.querySelector('.end-of-blog')
        observer.observe(target)
    },
    methods: {
        async get(endpoint, parameter, include=1, contentType) {
            const response = await fetch(`https://${process.env.VUE_APP_CONTENTFUL_ENVIRONMENT}.contentful.com/spaces/${SPACE_ID}/environments/${ENVIRONMENT_ID}/${endpoint}/${parameter}?access_token=${process.env.VUE_APP_CONTENTFUL_ACCESS_TOKEN}&include=${include}${contentType ? `&content_type=${contentType}` : ''}`)
            return await response.json()
        },
        getPosts() {
            this.get('entries', '', 2, 'blogPost')
            .then(posts => {
                this.includes = posts.includes || {}

                this.allPosts = (posts.items || [])
                    .filter(this.isFeatured)
                    .filter(this.isRenderable)
                    .map(this.decorate)
                    .sort((a, b) => b.fields.date - a.fields.date)

                // Push the next 10 posts to the posts array
                this.posts = this.allPosts.slice(0, 10)
            })
            .catch(error => {
                console.error('Could not load blog posts', error)
                this.allPosts = []
                this.posts = []
            })
        },
        // featured was added to the content model after the older posts were
        // written, so only an explicit false suppresses a post from the
        // listing — an absent flag still shows. Suppressed posts stay
        // reachable at /post/:id, they just aren't promoted here.
        isFeatured(post) {
            return Boolean(post && post.fields && post.fields.featured !== false)
        },
        // A post needs a title and a body to be worth a card. Everything else
        // (hero, author, avatar, date, read time) has a fallback below, so a
        // half-filled draft costs us one card rather than the whole listing.
        isRenderable(post) {
            const fields = post && post.fields
            return Boolean(fields && fields.title && fields.content)
        },
        decorate(post) {
            const author = this.getEntry(post.fields.author)

            post.fields.image = this.getAsset(post.fields.hero)
            post.fields.authorImage = this.getAsset(author && author.profilePicture)
            post.fields.author = (author && author.name) || 'The Gamebot Team'
            post.fields.date = new Date(post.fields.date || post.sys.createdAt)
            post.preview = this.getPreview(post.fields.content)
            return post
        },
        getPreview(content) {
            const blocks = (content && content.content) || []
            const text = blocks
                .filter(block => block.nodeType === 'paragraph')
                .map(block => (block.content || []).map(node => node.value || '').join(''))
                .join(' ')
                .trim()
            return text.length > 200 ? text.substring(0, 200) + '...' : text
        },
        updatePosts() {
            return this.posts = this.allPosts.slice(0, this.posts.length + 10)
        },
        getEntry(link) {
            const entries = this.includes.Entry || []
            const entry = link && entries.find(entry => entry.sys.id === link.sys.id)
            return entry ? entry.fields : null
        },
        getAsset(link) {
            // hero and profilePicture are no longer required in Contentful, so
            // the link — or the asset it points at — can be missing entirely
            const assets = this.includes.Asset || []
            const asset = link && assets.find(asset => asset.sys.id === link.sys.id)
            const file = asset && asset.fields && asset.fields.file
            return (file && file.url) || FALLBACK_IMAGE
        },
        setSize(url, w='', h='') {
            if(url === FALLBACK_IMAGE) return url
            if(w) w = '&w=' + w
            if(h) h = '&h=' + h
            return url + '?fm=jpg' + w + h
        },
        route(id) {
            this.$router.push(`/post/${id}#`)
        }
    },
    computed: {
    }
}
</script>